import React from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';

import { ConfigParametersStore } from '../../../stores/ConfigParametersStore';
import { BasicInput } from '../../../common/basic-input';
import { enforceParameters } from '../../../hocs/enforce-parameters';
import { Select } from '../../../common/select';
import { SecondaryButton } from '../../../common/secondary-button';
import { RecaptchaComponent } from '../../../common/recaptcha-component';
import { Validator } from '../../../helpers/Validator';
import { USER_TYPES } from '../../../models/User';
import { SessionStore } from '../../../stores/SessionStore';
import { ERROR_CODES } from '../../../models/ApiError';
import styles from './DataForm.css';

const IPropTypes = {
  className: PropTypes.string,
  userType: PropTypes.oneOf(Object.values(USER_TYPES)).isRequired,
};

const defaultProps = {
  className: '',
};

const DEFAULT_SELECTED_COUNTRY = 'Uruguay';

class DataForm extends React.Component {
  static createCountryOptions() {
    return ConfigParametersStore.parameters.countryDocuments.map(country => ({
      label: country.country,
      value: country.id,
    }));
  }

  constructor(props) {
    super(props);
    this.countryOptions = DataForm.createCountryOptions();
    this.state = {
      email: null,
      fictitiousName: null,
      names: null,
      password: null,
      personalId: null,
      rut: null,
      surnames: null,
      tradeName: null,
      recaptchaToken: null,
      recaptchaError: null,
      selectedCountry: this._getDefaultCountry(),

      emailValid: true,
      passwordValid: true,
      personalIdValid: true,
      rutValid: true,
    };

    this._getDefaultCountry = this._getDefaultCountry.bind(this);
    this._onRecaptchaError = this._onRecaptchaError.bind(this);
    this._onRecaptchaExpiry = this._onRecaptchaExpiry.bind(this);
    this._onRecaptchaSuccess = this._onRecaptchaSuccess.bind(this);
    this._submitData = this._submitData.bind(this);
    this._updateEmail = this._updateEmail.bind(this);
    this._updateFictitiousName = this._updateFictitiousName.bind(this);
    this._updateNames = this._updateNames.bind(this);
    this._updatePassword = this._updatePassword.bind(this);
    this._updatePersonalId = this._updatePersonalId.bind(this);
    this._updateRut = this._updateRut.bind(this);
    this._updateSelectedCountry = this._updateSelectedCountry.bind(this);
    this._updateSurnames = this._updateSurnames.bind(this);
    this._updateTradeName = this._updateTradeName.bind(this);
  }

  componentWillUnmount() {
    SessionStore.resetRegisterErrors();
  }

  _getDefaultCountry() {
    const defaultCountry = this.countryOptions
      .find(country => country.label === DEFAULT_SELECTED_COUNTRY);
    if (defaultCountry) {
      return defaultCountry.value;
    }
    return null;
  }

  _dataIsValid() {
    let specificDataIsValid = true;
    if (this.props.userType === USER_TYPES.COMPANY) {
      specificDataIsValid = (
        !!this.state.fictitiousName &&
        !!this.state.rut &&
        this.state.rutValid &&
        !!this.state.tradeName
      );
    } else if (this.props.userType === USER_TYPES.UNIPERSONAL) {
      specificDataIsValid = (
        !!this.state.names &&
        !!this.state.surnames &&
        !!this.state.selectedCountry &&
        !!this.state.personalId &&
        this.state.personalIdValid &&
        this.state.rutValid &&
        !!this.state.rut
      );
    } else {
      specificDataIsValid = (
        !!this.state.names &&
        !!this.state.surnames &&
        !!this.state.personalId &&
        this.state.personalIdValid &&
        !!this.state.selectedCountry
      );
    }
    return (
      !!this.state.email &&
      this.state.emailValid &&
      !!this.state.password &&
      this.state.passwordValid &&
      this.state.recaptchaToken &&
      specificDataIsValid
    );
  }

  _updateEmail(event) {
    const { target: { value } } = event;
    this.setState({
      email: value,
      emailValid: Validator.isEmail(value),
    });
  }

  _updateNames(event) {
    const { target: { value } } = event;
    this.setState({
      names: value,
    });
  }

  _updateSurnames(event) {
    const { target: { value } } = event;
    this.setState({
      surnames: value,
    });
  }

  _updatePersonalId(event) {
    const { target: { value } } = event;
    // Only alpha-numeric characters allowed
    if (!/^\w*$/.test(value)) {
      return;
    }
    this.setState((prevState) => {
      let personalIdValid = true;
      if (prevState.selectedCountry === 14) {
        personalIdValid = Validator.isUruguayanId(value);
      }
      return {
        personalId: value,
        // If it is an uruguayan Id, verify it
        personalIdValid,
      };
    });
  }

  _updateRut(event) {
    const { target: { value } } = event;
    // Only numeric characters allowed
    if (!/^\d*$/.test(value)) {
      return;
    }
    this.setState({
      rut: value,
      rutValid: Validator.isUruguayanRUTNew(value),
    });
  }

  _updateTradeName(event) {
    const { target: { value } } = event;
    this.setState({
      tradeName: value,
    });
  }

  _updateFictitiousName(event) {
    const { target: { value } } = event;
    this.setState({
      fictitiousName: value,
    });
  }

  _updatePassword(event) {
    const { target: { value } } = event;
    this.setState({
      password: value,
      passwordValid: Validator.passwordIsValid(value),
    });
  }

  _updateSelectedCountry(value) {
    this.setState({
      selectedCountry: value,
    });
  }

  _onRecaptchaSuccess(token) {
    this.setState({
      recaptchaError: false,
      recaptchaToken: token,
    });
  }

  _onRecaptchaError() {
    this.setState({
      recaptchaError: true,
    });
  }

  _onRecaptchaExpiry() {
    this.setState({
      recaptchaToken: null,
    });
  }

  _submitData(event) {
    event.preventDefault();
    if (!this._dataIsValid()) {
      return;
    }
    SessionStore.register(this.props.userType, {
      email: this.state.email,
      fictitiousName: this.state.fictitiousName,
      names: this.state.names,
      password: this.state.password,
      personalId: this.state.personalId,
      recaptchaToken: this.state.recaptchaToken,
      rut: this.state.rut,
      selectedCountry: this.state.selectedCountry,
      surnames: this.state.surnames,
      tradeName: this.state.tradeName,
    });
  }

  render() {
    let emailError = null;
    if (this.state.email === '') {
      emailError = 'El email no puede ser vacío';
    } else if (!this.state.emailValid) {
      emailError = 'El email es inválido';
    }
    let rutError = null;
    if (this.state.rut === '') {
      rutError = 'El RUT no puede ser vacío';
    } else if (!this.state.rutValid || SessionStore.registerApiError === ERROR_CODES.INVALID_RUT) {
      rutError = 'El RUT es inválido';
    }
    let passwordError = null;
    if (this.state.password === '') {
      passwordError = 'La contraseña no puede ser vacía';
    } else if (!this.state.passwordValid) {
      passwordError = 'La contraseña debe tener al menos 6 caracteres';
    }
    let personalIdError = null;
    if (this.state.personalId === '') {
      personalIdError = 'Este campo no puede ser vacío';
    } else if (!this.state.personalIdValid ||
        SessionStore.registerApiError === ERROR_CODES.INVALID_URUGUAYAN_DOCUMENT) {
      personalIdError = 'El documento es inválido';
    }
    const namesError = this.state.names === '' ? 'Este campo no puede ser vacío' : null;
    const surnamesError = this.state.surnames === '' ? 'Este campo no puede ser vacío' : null;
    const tradeNameError = this.state.tradeName === '' ? 'Este campo no puede ser vacío' : null;
    const fictitiousNameError = this.state.fictitiousName === '' ? 'Este campo no puede ser vacío' : null;

    const countryValue = this.state.selectedCountry
      ? this.countryOptions.find(country => country.value === this.state.selectedCountry).label
      : 'Seleccionar País';
    return (
      <form
        className={this.props.className}
        onSubmit={this._submitData}
      >
        <BasicInput
          className={styles.input}
          error={emailError}
          label="Correo electrónico"
          name="email"
          type="email"
          onChange={this._updateEmail}
        />
        {
          this.props.userType !== USER_TYPES.COMPANY &&
          <div>
            <BasicInput
              className={styles.input}
              error={namesError}
              label="Nombres"
              name="names"
              onChange={this._updateNames}
            />
            <BasicInput
              className={styles.input}
              error={surnamesError}
              label="Apellidos"
              name="surnames"
              onChange={this._updateSurnames}
            />
            <Select
              className={styles.select}
              options={this.countryOptions}
              onChange={this._updateSelectedCountry}
              value={countryValue}
            />
            <BasicInput
              className={styles.input}
              error={personalIdError}
              disabled={!this.state.selectedCountry}
              label="Número de documento"
              name="personalId"
              onChange={this._updatePersonalId}
              value={this.state.personalId || ''}
            />
          </div>
        }
        {
          this.props.userType === USER_TYPES.COMPANY &&
          <div>
            <BasicInput
              className={styles.input}
              error={tradeNameError}
              label="Razón social"
              name="tradeName"
              onChange={this._updateTradeName}
            />
            <BasicInput
              className={styles.input}
              error={fictitiousNameError}
              label="Nombre fantasía"
              name="fictitiousName"
              onChange={this._updateFictitiousName}
            />
          </div>
        }
        {
          this.props.userType !== USER_TYPES.PERSON &&
          <BasicInput
            className={styles.input}
            error={rutError}
            label="RUT"
            name="rut"
            onChange={this._updateRut}
            value={this.state.rut || ''}
          />
        }
        <BasicInput
          className={styles.input}
          error={passwordError}
          label="Crea tu contraseña"
          name="password"
          type="password"
          onChange={this._updatePassword}
        />

        <div className={styles.recaptcha}>
          {
            !!this.state.recaptchaError &&
            <p className={styles.errorMessage}>
              Ha ocurrido un error, intente realizar el reCAPTCHA de nuevo.
            </p>
          }
          <RecaptchaComponent
            onError={this._onRecaptchaError}
            onExpiry={this._onRecaptchaExpiry}
            onSuccess={this._onRecaptchaSuccess}
          />
        </div>
        {
          !!SessionStore.registerAuthError &&
          <p className={styles.error}>
            El usuario ha sido creado, pero no se pudo completar la autenticación.
            Por favor, intente iniciar sesión nuevamente. Si el problema persiste,
            contacte con nuestro área de atención al cliente para recibir asistencia.
          </p>
        }
        {
          !!SessionStore.registerInternalError &&
          <p className={styles.error}>
            Estamos experimentando algunas dificultades en nuestro sitio.
            Por favor intente nuevamente en unos momentos.
          </p>
        }
        {
          !!SessionStore.registerError &&
          <p className={styles.error}>
            Ha ocurrido un error, por favor intente nuevamente.
          </p>
        }
        {
          SessionStore.registerApiError === ERROR_CODES.CLIENT_ALREADY_EXISTS &&
          <p className={styles.error}>
            Ya se encuentra un usuario registrado con los mismos datos.
          </p>
        }

        <SecondaryButton
          className={styles.button}
          disabled={!this._dataIsValid()}
        >
          Registrarme
        </SecondaryButton>
      </form>
    );
  }
}

DataForm.propTypes = IPropTypes;
DataForm.defaultProps = defaultProps;

const ObservedDataForm = enforceParameters(observer(DataForm));

export { ObservedDataForm as DataForm };



// WEBPACK FOOTER //
// ./src/pages/register/data-form/DataForm.jsx